import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles/useOptionGroupStyles'
import {
    getDisabledStyleClasses,
    nullOrUndefinedResolver,
} from 'components/WorkspaceEditor/utils'
import {
    useProjectSettings,
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo, useCallback } from 'react'
import sanitizeHtml from 'sanitize-html'
import { countSpecialChars } from '../ContentEditor/contentUtils'
import { ReplacementOption } from './ReplacementOption'
import { SelectionOption } from './SelectionOption'

const UOM_MAX_LENGTH = 20
const UOM_MAX_SPECIAL_CHARS = 10

/** Props for the UnitsOfMeasure component. */
export interface UnitsOfMeasureProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is disabled (not hidden, just styled as disabled) */
    disabled?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The parent content item view node — when provided, enables inline editing */
    parentContentItem?: Partial<TextContentElement>
}
/**
 * A component that apply styling to render UnitsOfMeasure content
 * @param {Partial<TextContentElement>} contentItem - the object that holds the text property that needs to render imperial and metric units.
 */
export const UnitsOfMeasure = memo<UnitsOfMeasureProps>(
    ({ contentItem, inactive = false, disabled = false, editable = false, parentContentItem }) => {
        const { id, imperialUnits, metricUnits } = nullOrUndefinedResolver(
            contentItem
        )
        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            disabledBoxedInput,
            disabledPill,
            disabledOptionGroup,
            notDisabledBoxedInputContainer,
            uomContainerInlineFlex,
            uomContainerContent,
            uomGroupWrapper,
            uomMetricUnitWrapper
        } = useOptionGroupStyles()
        const isBreakWord = imperialUnits.length >= 50

        // Get disabled styling classes using shared helper
        const disabledClasses = getDisabledStyleClasses(
            disabled,
            inactive,
            editable,
            false, // isInActiveOptionItem - not applicable for UnitsOfMeasure
            {
                disabledPill,
                disabledBoxedInput,
                disabledOptionGroup,
                disabledBoxedInputContainer,
                notDisabledBoxedInputContainer,
            }
        )

        const optionClasses = clsx(
            editableText,
            editableTextBoxed,
            disabledClasses
        )
        const containerClass = isBreakWord
            ? uomContainerContent
            : uomContainerInlineFlex
        const projectSettings = useProjectSettings()
        let unitOfMeasure =
            projectSettings?.unitOfMeasure.toString() ?? 'IMPERIAL'
        const upperCaseUOM = unitOfMeasure.toUpperCase()
        const isBothUnitsActive = upperCaseUOM === 'BOTH'
        const isImperialUnitsActive = upperCaseUOM === 'IMPERIAL'
        const isMetricUnitsActive = upperCaseUOM === 'METRIC'

        const { mutateContentElements } = useContentMutationService()
        const { executeWithOptimistic } = useTransactionManager()
        const { fetchTrackChangesDataWithApolloClient } = useTrackChangesQuery()
        const { setContentItemViewNodes } = useShallowSelector((s) => ({
            setContentItemViewNodes: s.actions.setContentItemViewNodes,
        }))

        const handleUOMChange = useCallback(
            (
                updatedItem: Partial<TextContentElement>,
                unitType: 'imperial' | 'metric'
            ) => {
                if (!contentItem.id || !parentContentItem?.id) return

                const sanitized = sanitizeHtml(updatedItem.text?.trim() ?? '', {
                    allowedTags: [],
                    allowedAttributes: {},
                })
                // Use DOMParser to decode all HTML entities in a single pass (handles all entities correctly)
                const newValue =
                    new DOMParser().parseFromString(sanitized, 'text/html').body.textContent ?? ''

                if (newValue.length > UOM_MAX_LENGTH || countSpecialChars(newValue) > UOM_MAX_SPECIAL_CHARS) return

                const updatedUOM: Partial<TextContentElement> =
                    unitType === 'imperial'
                        ? { ...contentItem, imperialUnits: newValue, changeType: 'MODIFIED' }
                        : { ...contentItem, metricUnits: newValue, changeType: 'MODIFIED' }

                // Read current nodes at execution time to avoid stale closure
                const currentNodes = useWorkspace2Store.getState().contentItemViewNodes

                // Optimistic update: replace the UOM element in the parent node's elements
                setContentItemViewNodes(currentNodes.map((node) =>
                    node.id === parentContentItem.id
                        ? {
                              ...node,
                              elements: (node.elements || []).map((el) =>
                                  el.id === contentItem.id ? updatedUOM : el
                              ),
                          }
                        : node
                ))

                // executeWithOptimistic handles "Saving..." / "Changes Saved Successfully" toasts
                // and rollback on error via its internal transaction snapshot — no errorCbFn needed.
                executeWithOptimistic({
                    fallbackState: '',
                    asyncAction: () => mutateContentElements(contentItem.id!, [updatedUOM]),
                    successCbFn: () => {
                        fetchTrackChangesDataWithApolloClient()
                    },
                })
            },
            [contentItem, parentContentItem, setContentItemViewNodes, mutateContentElements, executeWithOptimistic, fetchTrackChangesDataWithApolloClient]
        )

        const isInlineEditable = editable && !!parentContentItem

        const renderOption = (unitValue: string, type: 'imperial' | 'metric') => {
            // Unique id per unit to avoid CKEditor toggle conflicts between imperial and metric
            const virtualId = type === 'metric' ? `${id}-metric` : id

            return (
                <div
                    key={`units-of-measure-${type}-${id || 'default'}`}
                    data-testid={`units-of-measure-${type}-${id || 'default'}`}
                    className={containerClass}
                >
                    {isInlineEditable ? (
                        <ReplacementOption
                            contentItem={{
                                ...contentItem,
                                id: virtualId,
                                text: unitValue,
                                elements: [],
                            }}
                            classes={optionClasses}
                            disabled={disabled}
                            editable={editable}
                            maxLength={UOM_MAX_LENGTH}
                            maxSpecialChars={UOM_MAX_SPECIAL_CHARS}
                            onValueChange={(updatedItem) =>
                                handleUOMChange(updatedItem, type)
                            }
                        />
                    ) : (
                        <SelectionOption
                            contentItem={contentItem}
                            label={unitValue}
                            classes={optionClasses}
                            disabled={disabled}
                            editable={editable}
                        />
                    )}
                </div>
            )
        }

        return (
            <span className={uomGroupWrapper} data-suggestion={`units-of-measure-${id || 'default'}`}>
                {(isImperialUnitsActive || isBothUnitsActive) &&
                    renderOption(imperialUnits, 'imperial')}
                {isMetricUnitsActive && renderOption(metricUnits, 'metric')}
                {isBothUnitsActive && (
                    <>
                        <span className={uomMetricUnitWrapper}>(</span>
                        {renderOption(metricUnits, 'metric')}
                        <span>)</span>
                    </>
                )}
            </span>
        )
    }
)
